'use client';

import { Modal } from "react-bootstrap";
import classes from "./favorite_modal.module.css";
import FeaturedIcon from "../icons/featured_icon";
import Heart from '@/assets/svg/icons/heart.svg';
import Save from '@/assets/svg/icons/save.svg';
import { useContext, useEffect, useRef, useState } from "react";
import TextAreaInput from "../inputs/text_editor_input";
import CustomButton from "../buttons/button";
import { useMutation, useQuery } from "@tanstack/react-query";
import { changeInlineProvider, getOneProvider } from "@/utils/http";
import { useRouter } from 'next/navigation';
import ToastContext from "@/providers/toast-context";
import Trash from '@/assets/svg/icons/trash.svg';
import Shuffle from '@/assets/svg/icons/shuffle.svg';
import { getProviderStatuses } from "@/variables/variables";
import { ProviderItemBadge, ProviderItemBadgeContainer } from "@/components/provider/provider_item";
import { useMobileApp } from "@/providers/mobile-app-context";

const provider_statuses = getProviderStatuses();
const PENDING_FAVORITE_LOGIN_ACTION_KEY = "pending_favorite_login_action";
const PENDING_FAVORITE_OPEN_MODAL_KEY = "pending_favorite_open_modal";

const sameProviderId = (left, right) => Number(left) === Number(right);
const isActiveFavouriteStatus = (status) => status === "like" || status === "selected";


export default function FavoriteModal({ id, setId, weddingId, setShowLogin, setShowLoginState, setRedirectURL, tempFavouriteIds, setTempFavouriteIds, tempFavouriteNotes, setTempFavouriteNotes, status }) {
    const router = useRouter();
    const { isNativeApp } = useMobileApp();
    const toastCtx = useContext(ToastContext);
    const [providerStatus, setProviderStatus] = useState("");
    const [note, setNote] = useState("");
    const [originallyFavourite, setOriginallyFavourite] = useState(false);
    const pendingFavoriteApplyRef = useRef(false);

    const { mutate: changeInline, mutateAsync: changeInlineAsync } = useMutation({
        mutationFn: changeInlineProvider
    });

    const { data: providerDetails } = useQuery({
        queryKey: ['providers', [weddingId, id]],
        queryFn: () => getOneProvider({wedding_id: weddingId, provider_id: id}),
        initialData: {id, status:"", note: ""},
        enabled: (id !== 0)
    });

    const close = () => {
        setId(0);
        setNote("");
    }

    const redirectToWedding = () => {
        router.push(`/szervezom/0`);
    }

    const statusChangeHandler = (e, id, field, val) => {
        setProviderStatus(val);
    }
    
    const save = () => {
        if(providerStatus === "") {
            toastCtx.addToast({type: "error", header_text: "Kedvenc szolgáltató", sub_text: "Kérlek, válassz egy státuszt a mentéshez!"});
            return;
        }

        changeInline({ provider_id: id, wedding_id: weddingId, field: "status", val: providerStatus == "remove" ? "archiv" : providerStatus });
        setTimeout(() => {
            changeInline({ provider_id: id, wedding_id: weddingId, field: "note", val: note });
        }, 1000);

        if (isActiveFavouriteStatus(providerStatus)) {
            setTempFavouriteIds(prev => {
                if (!prev.some(favouriteId => sameProviderId(favouriteId, id))) {
                    return [...prev, id];
                } else {
                    return prev;
                }
            });
        } else {
            setTempFavouriteIds(prev => prev.filter(p => !sameProviderId(p, id)));
        }
        setTempFavouriteNotes(prev => {
            if (!prev.find(x => sameProviderId(x.id, id))) {
                return [...prev, { id: id, note: note }];
            } else {
                return prev.map(p => {
                    if (sameProviderId(p.id, id)) {
                        return { id: p.id, note: note };
                    }
                    return p;
                });
            }
        });

        toastCtx.addToast({type: "success", header_text: "Kedvenc szolgáltató", sub_text: "A módosítás sikeresen megtörtént."});
        close();
    }
 
    useEffect(() => {
        setProviderStatus(providerDetails?.status || "");
    }, [providerDetails])

    useEffect(() => {
        if (id !== 0) {
            setOriginallyFavourite(tempFavouriteIds?.some(favouriteId => sameProviderId(favouriteId, id)));

            if (tempFavouriteNotes) {
                const favNote = tempFavouriteNotes.find((note) => sameProviderId(note.id, id));
                if (favNote) {
                    setNote(favNote.note);
                } else {
                    setNote("");
                }
            }
        }
    }, [id, tempFavouriteIds, tempFavouriteNotes])

    useEffect(() => {
        if (status !== "authenticated" || weddingId <= 0 || pendingFavoriteApplyRef.current) return;

        let pendingRaw = "";
        try {
            pendingRaw = sessionStorage.getItem(PENDING_FAVORITE_LOGIN_ACTION_KEY);
        } catch (_) {
            pendingRaw = "";
        }

        if (!pendingRaw) return;

        let pendingData = null;
        try {
            pendingData = JSON.parse(pendingRaw);
        } catch (_) {
            pendingData = null;
        }

        const pendingProviderId = Number(pendingData?.providerId);
        if (!Number.isFinite(pendingProviderId) || pendingProviderId <= 0) {
            try { sessionStorage.removeItem(PENDING_FAVORITE_LOGIN_ACTION_KEY); } catch (_) {}
            return;
        }

        pendingFavoriteApplyRef.current = true;

        (async () => {
            try {
                await changeInlineAsync({ provider_id: pendingProviderId, wedding_id: weddingId, field: "status", val: "like" });
                await changeInlineAsync({ provider_id: pendingProviderId, wedding_id: weddingId, field: "note", val: "" });

                setTempFavouriteIds(prev => prev.some(favouriteId => sameProviderId(favouriteId, pendingProviderId)) ? prev : [...prev, pendingProviderId]);
                setTempFavouriteNotes(prev => prev.find(item => sameProviderId(item.id, pendingProviderId)) ? prev : [...prev, { id: pendingProviderId, note: "" }]);

                // A modal nyitását jelöljük, és késleltetve nyitjuk egy külön effectben,
                // így nem villan fel majd tűnik el a login utáni redirect közben.
                try {
                    sessionStorage.setItem(PENDING_FAVORITE_OPEN_MODAL_KEY, JSON.stringify({
                        providerId: pendingProviderId,
                        scrollY: pendingData?.scrollY
                    }));
                } catch (_) {}
            } catch (error) {
                console.error("Pending favorite post-login apply failed", error);
            } finally {
                try { sessionStorage.removeItem(PENDING_FAVORITE_LOGIN_ACTION_KEY); } catch (_) {}
                pendingFavoriteApplyRef.current = false;
            }
        })();
    }, [status, weddingId, changeInlineAsync, setTempFavouriteIds, setTempFavouriteNotes]);

    useEffect(() => {
        if (status !== "authenticated" || weddingId <= 0 || id !== 0) return;

        let openRaw = "";
        try {
            openRaw = sessionStorage.getItem(PENDING_FAVORITE_OPEN_MODAL_KEY);
        } catch (_) {
            openRaw = "";
        }

        if (!openRaw) return;

        let openData = null;
        try {
            openData = JSON.parse(openRaw);
        } catch (_) {
            openData = null;
        }

        const openProviderId = Number(openData?.providerId);
        if (!Number.isFinite(openProviderId) || openProviderId <= 0) {
            try { sessionStorage.removeItem(PENDING_FAVORITE_OPEN_MODAL_KEY); } catch (_) {}
            return;
        }

        const timer = setTimeout(() => {
            if (Number.isFinite(openData?.scrollY)) {
                const targetY = Math.max(0, Number(openData.scrollY));
                window.scrollTo(0, targetY);
            }

            setId(openProviderId);
            try { sessionStorage.removeItem(PENDING_FAVORITE_OPEN_MODAL_KEY); } catch (_) {}
        }, 700);

        return () => clearTimeout(timer);
    }, [status, weddingId, id, setId]);

    const isExistingFavourite = isActiveFavouriteStatus(providerDetails?.status) || tempFavouriteIds?.some(favouriteId => sameProviderId(favouriteId, id));
    const statusOptionsSource = isExistingFavourite
        ? {...provider_statuses, remove: {name: " - törlöm a listámból - ", type: "warning", icon: Trash}}
        : provider_statuses;

    let status_options = [];
    for(let p in statusOptionsSource) {
        if(p !== "archiv") status_options.push({val: p, name: <ProviderItemBadge text={statusOptionsSource[p].name} color={statusOptionsSource[p].type} inner_svg={statusOptionsSource[p].icon} />});
    }

    return <Modal show={id !== 0} onHide={close} className={`${classes.modal} ${isNativeApp ? classes["native-modal"] : ""}`} dialogClassName={`${classes["modal-dialog"]} ${isNativeApp ? classes["native-dialog"] : ""}`} contentClassName={`${classes["modal-content"]} ${isNativeApp ? classes["native-content"] : ""}`}>
        <div className={`${classes["modal-main-content"]}`}>
            {isNativeApp && <div className={classes["sheet-handle"]} />}
            <div className="d-flex flex-column align-items-start align-self-stretch gap-5">
                <div className="d-flex flex-row align-items-center gap-5 w-100">
                    <FeaturedIcon size="lg" color={"primary-strong"} icon={Heart} outline />
                    <p className="margin-0 color-gray-900 text-lg-semibold flex-grow-1 text-truncate">{originallyFavourite ? 'Kedvenc szerkesztése' : 'Hozzáadás kedvencekhez'}</p>
                </div>
                {status === "unauthenticated" && <p className="margin-0 color-gray-500 text-sm-regular">
                    Nem vagy bejelentkezve, így jelenleg nem rögzítheted Kedvenc szolgáltatóidat!
                </p>}

                {status === "authenticated" && weddingId > 0 && id !== 0 &&
                    <>
                        <div className="d-flex flex-column align-items-start" >
                            <ProviderItemBadgeContainer title={"Státusz"} text={statusOptionsSource[providerStatus]?.name || "Válassz a lehetőségek közül"} color={statusOptionsSource[providerStatus]?.type || "gray"}
                            inner_svg={statusOptionsSource[providerStatus]?.icon || Shuffle} selected={providerStatus} options={providerStatus === "archiv" ? undefined : status_options}
                            selectHandler={(e, val) => statusChangeHandler(e, id, "status", val)} />
                        </div>
                        <TextAreaInput id="add-favorite-text-area" type="simple" value={note} label_color={"color-gray-800"} label_font={"text-md-regular"} onChange={e => setNote(e)} label={"Megjegyzés"} placeholder={"Megjegyzés a szolgáltatóval kapcsolatosan"} height={150} style={{ height: 150 }} />
                    </>
                }
                {status === "authenticated" && weddingId === 0 &&
                    <>
                        <p className="margin-2 color-gray-500 text-md-regular mb-3">
                            Indítsd el esküvődet, hogy a kedvenc szolgáltatóidat rögzíthesd!
                        </p>
                        <CustomButton className="w-100" size="lg" hierarchy="primary" type="button" onClick={() => { close(); redirectToWedding(); }} >
                            Irány az esküvőm
                        </CustomButton>
                    </>
                }

            </div>
            <div className="d-flex flex-column align-items-center align-self-stretch gap-2">
                {status === "unauthenticated" && <>
                    <CustomButton className="w-100" size="lg" hierarchy="primary" type="button" onClick={() => {
                        const sourceUrl = `${window.location.pathname}${window.location.search}`;
                        try {
                            sessionStorage.setItem(PENDING_FAVORITE_LOGIN_ACTION_KEY, JSON.stringify({ providerId: id, sourceUrl, scrollY: window.scrollY, createdAt: Date.now() }));
                        } catch (_) {}
                        close();
                        setShowLogin(true);
                        setShowLoginState("login");
                        setRedirectURL(sourceUrl);
                    }} >
                        Bejelentkezés
                    </CustomButton>
                    <CustomButton className="w-100" size="lg" hierarchy="tertiary-gray" type="button" onClick={close} >
                        Később
                    </CustomButton>
                </>}
            </div>
            {status === "unauthenticated" && <p className="margin-0 padding-0 color-gray-700 text-sm-regular d-flex align-self-stretch gap-1">
                Még nem rendelkezel fiókkal? <span className="margin-0 padding-0 color-gray-700 text-sm-semibold pointer" onClick={() => {
                    const sourceUrl = `${window.location.pathname}${window.location.search}`;
                    try {
                        sessionStorage.setItem(PENDING_FAVORITE_LOGIN_ACTION_KEY, JSON.stringify({ providerId: id, sourceUrl, scrollY: window.scrollY, createdAt: Date.now() }));
                    } catch (_) {}
                    close();
                    setShowLogin(true);
                    setShowLoginState("registration");
                    setRedirectURL(sourceUrl);
                }}>Regisztráció</span>
            </p>}
            {status === "authenticated" && weddingId > 0 &&
                <CustomButton className="w-100" size="lg" hierarchy="primary" icon="leading" is_svg={true} svg={Save} type="button" onClick={() => { save(); }} >
                    Mentés és bezárás
                </CustomButton>
            }
        </div>
    </Modal>
}